import React from 'react'
import { X, Building2, Mail, AlertCircle } from 'lucide-react'

interface CompanyInfo {
  id: string
  title: string
  email?: string
  iin: string
}

interface UserData {
  name: string
  surname: string
  phone: string
  email?: string
}

interface CompanyJoinConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  onCancel: () => void
  company: CompanyInfo
  userData: UserData
  isLoading?: boolean
}

export const CompanyJoinConfirmationModal: React.FC<CompanyJoinConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onCancel,
  company,
  userData,
  isLoading = false
}) => {
  if (!isOpen) return null

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose()
    }
  }

  const handleCancel = () => {
    if (!isLoading) {
      onCancel()
      onClose()
    }
  }

  const handleConfirm = () => {
    if (!isLoading) {
      onConfirm()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.4)",
        backdropFilter: "blur(8px)"
      }}
      onClick={handleBackdropClick}
    >
      {/* Модальное окно */}
      <div 
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl"
        style={{
          maxHeight: '90vh',
          overflow: 'auto'
        }}
      >
        {/* Заголовок */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Вас нет в системе компании
              </h2>
              <p className="text-sm text-gray-500">
                Требуется одобрение администратора
              </p>
            </div>
          </div>
          {!isLoading && (
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              title="Закрыть"
            >
              <X size={20} />
            </button>
          )}
        </div>

        {/* Основной контент */}
        <div className="p-6 space-y-6">
          {/* Информация о компании */}
          <div className="bg-blue-50 rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <Building2 className="w-4 h-4 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-blue-900">
                  {company.title}
                </h3>
                <p className="text-xs text-blue-700 mt-1">
                  ИНН: {company.iin}
                </p>
                
              </div>
            </div>
          </div>

          {/* Информация о пользователе */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              Ваши данные для заявки:
            </h4>
            <div className="space-y-1 text-sm text-gray-600">
              <p><span className="font-medium">Имя:</span> {userData.name} {userData.surname}</p>
              <p><span className="font-medium">Телефон:</span> {userData.phone}</p>
              {userData.email && (
                <p><span className="font-medium">Email:</span> {userData.email}</p>
              )}
            </div>
          </div>

          {/* Объяснение процесса */}
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
                <Mail className="w-3 h-3 text-green-600" />
              </div>
              <div className="text-sm text-gray-600">
                <p className="font-medium text-gray-900 mb-1">Что произойдет дальше:</p>
                <p>Администратор компании получит заявку с вашими данными и сможет подтвердить или отклонить её.</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                <span className="text-xs font-bold text-blue-600">2</span>
              </div>
              <div className="text-sm text-gray-600">
                <p>После рассмотрения заявки вы получите уведомление на email о результате.</p>
              </div>
            </div>
          </div>

          {/* Предупреждение */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">
                <span className="font-medium">Важно:</span> Заявка будет отправлена администратору компании. 
                
              </p>
            </div>
          </div>
        </div>

        {/* Кнопки действий */}
        <div className="flex space-x-3 p-6 border-t border-gray-100">
          <button
            onClick={handleCancel}
            disabled={isLoading}
            style={{
              flex: 1,
              padding: '0.75rem 1rem',
              background: '#f3f4f6',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontWeight: 600,
              fontSize: '0.875rem',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              opacity: isLoading ? 0.5 : 1
            }}
          >
            Отмена
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            style={{
              flex: 1,
              padding: '0.75rem 1rem',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 600,
              fontSize: '0.875rem',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              background: isLoading ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              opacity: isLoading ? 0.6 : 1
            }}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Отправляем...</span>
              </>
            ) : (
              <>
                <Mail className="w-4 h-4" />
                <span>Отправить заявку</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
